import { useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

async function postJson(path, payload) {
  const res = await axios.post(`${API}${path}`, payload);
  return res.data;
}

function readError(err) {
  return err?.response?.data?.detail || "Something went wrong. Please try again.";
}

// Minimal form submission hook: status is "idle" | "loading" | "ok" | "error".
export default function useFormSubmit(path, options = {}) {
  const { resetTo } = options;
  const [status, setStatus] = useState("idle");
  const [errMsg, setErrMsg] = useState("");

  // Deps include the module-scope helpers (`postJson`, `readError`) and the
  // stable React setters (`setStatus`, `setErrMsg`) — all stable references,
  // so this list is effectively constant but satisfies exhaustive-deps.
  const submit = useCallback(
    async (payload, setForm) => {
      setStatus("loading");
      setErrMsg("");
      try {
        await postJson(path, payload);
        setStatus("ok");
        if (setForm && resetTo) setForm(resetTo);
        return true;
      } catch (err) {
        setStatus("error");
        setErrMsg(readError(err));
        return false;
      }
    },
    [path, resetTo, setStatus, setErrMsg],
  );

  // Clear any prior success/error banner — call on user input.
  const reset = useCallback(() => {
    setStatus((prev) => (prev === "loading" ? prev : "idle"));
    setErrMsg("");
  }, [setStatus, setErrMsg]);

  return { status, errMsg, submit, reset };
}
